import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StudentLayout } from './StudentLayout';
import { TeacherLayout } from './TeacherLayout';
interface RoleAwareLayoutProps {
  children: React.ReactNode;
}
/**
 * RoleAwareLayout - Renders the appropriate layout based on user role
 *
 * This component prevents layout mixing by ensuring:
 * - Students always see StudentLayout (with student sidebar)
 * - Teachers always see TeacherLayout (with teacher sidebar)
 * - Teachers must have verified email to access these pages
 * - Unauthenticated users are redirected to the landing page
 *
 * Used for shared account pages that both roles can access.
 */
export const RoleAwareLayout: React.FC<RoleAwareLayoutProps> = ({
  children
}) => {
  const {
    currentUser,
    userType,
    loading
  } = useAuth();
  const navigate = useNavigate();
  // STEP 5: Redirect unauthenticated users to landing page
  useEffect(() => {
    if (!loading && !currentUser) {
      console.log('[RoleAwareLayout] No authenticated user, redirecting to /');
      navigate('/', {
        replace: true
      });
    }
  }, [currentUser, loading, navigate]);
  // Enforce email verification for teachers accessing account pages
  useEffect(() => {
    if (!loading && currentUser && userType === 'teacher') {
      if (!currentUser.emailVerified) {
        console.log('[RoleAwareLayout] Teacher email not verified, redirecting to verification page');
        navigate('/teacher-email-verification', {
          replace: true
        });
      }
    }
  }, [currentUser, userType, loading, navigate]);
  // Show loading spinner while checking auth state
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-1518">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-deep-pink" data-id="element-1519"></div>
      </div>;
  }
  // STEP 5: Don't render layout if user is not authenticated
  if (!currentUser) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-1520">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-deep-pink" data-id="element-1521"></div>
      </div>;
  }
  // Show loading spinner while checking email verification for teachers
  if (userType === 'teacher' && !currentUser.emailVerified) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-1522">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-deep-pink" data-id="element-1523"></div>
      </div>;
  }
  // Normalize userType to handle case variations
  const normalizedUserType = userType ? userType.toLowerCase().trim() : null;
  const isStudent = normalizedUserType === 'student';
  // Students get StudentLayout, everyone else gets TeacherLayout
  return isStudent ? <StudentLayout data-id="element-1524">{children}</StudentLayout> : <TeacherLayout data-id="element-1525">{children}</TeacherLayout>;
};